import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Paper,
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Skeleton,
    Typography,
    Slide,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {CompanyAnnualPage, companyAnnualPage, deleteAnnual, RequestCompanyAnnualPage} from '@/services/accountService/companyAnnualService';
import {ResponsePage} from '@/services/page.interface';
import CompanyAnnualDetail from "@/components/company-info/company-annual/CompanyAnnualDetail";
import {SnackbarField, PopoverButton, InputField} from '@/components/common';

export default function CompanyAnnualList() {
    const theme = useTheme();
    const principalActivitiesMap: Record<string, string> = useMemo(() => ({
        '0': 'Inactive',
        '1': 'General Trading',
        '2': 'Services',
        '3': 'Trading And Services',
    }), []);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [data, setData] = useState<ResponsePage<CompanyAnnualPage> | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedAnnualId, setSelectedAnnualId] = useState<number | null>(null);
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
    const [isAdd, setIsAdd] = useState<boolean>(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message?: string;
        severity?: 'success' | 'error';
    }>({open: false});
    const [filterCompanyName, setFilterCompanyName] = useState('');
    const [filterYearOfAssessment, setFilterYearOfAssessment] = useState('');
    const [expandedCompany, setExpandedCompany] = useState<string | null>(null);

    /**
     * 拉取年度列表。
     * @param opts.silent true=无感刷新（保留当前行，不打龙骨）；false/缺省=整表 Skeleton（菜单进入、筛选、翻页）
     */
    const fetchData = async (opts?: {silent?: boolean}) => {
        const silent = !!opts?.silent;
        // WHY: Detail 返回/删除后列表已在屏上，避免 Skeleton 闪屏
        if (!silent) {
            setLoading(true);
        }
        try {
            const req: RequestCompanyAnnualPage = {
                pageNumber: page + 1,
                pageSize: rowsPerPage,
                pageType: 1,
                companyName: filterCompanyName || undefined,
                yearOfAssessment: filterYearOfAssessment || undefined,
            };
            const res = await companyAnnualPage(req);
            setData(res);
            setError(null);
        } catch (e: any) {
            setError(e?.message || 'Failed to load annual list');
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchData();
        }, 400);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCompanyName, filterYearOfAssessment]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    const rows = data?.list || [];
    const total = data?.totalRow || 0;

    return (
        <Slide direction="left" in={true} mountOnEnter unmountOnExit>
            <Box sx={{
                p: '20px',
                height: 'calc(100vh - 120px)',
                minHeight: '70vh',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexShrink={0}>
                    <Typography variant="h6" fontWeight={700} color="text.primary">
                        Company Annual Information
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <InputField size="small" label="Company Name" value={filterCompanyName} onChange={e => {
                            setPage(0);
                            setFilterCompanyName(e.target.value);
                        }}/>
                        <InputField size="small" label="Assessment Year" value={filterYearOfAssessment}
                                   placeholder="e.g. 2024/25"
                                   onChange={e => {
                                       setPage(0);
                                       setFilterYearOfAssessment(e.target.value);
                                   }}/>
                    {/*{canCreate && (*/}
                    <Button
                        size='small'
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            setIsAdd(true);
                            setSelectedAnnualId(null);
                            setDetailOpen(true);
                        }}
                        sx={{
                            borderRadius: theme.shape.borderRadius,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: theme.shadows[1],
                            '&:hover': {boxShadow: theme.shadows[3]}
                        }}
                    >
                        Add
                    </Button>
                    {/*)}*/}
                </Stack>
                </Stack>

                {error && (
                    <Box sx={{color: 'error.main', px: 1, py: 0.5, fontSize: 12, flexShrink: 0}}>{error}</Box>
                )}

                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        boxShadow: theme.shadows[2],
                        bgcolor: 'background.paper',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            // Align content-row height with FsAdjustment (BS/PL leaf rows)
                            '& .MuiTableCell-root': {
                                paddingTop: '5px',
                                paddingBottom: '5px',
                            },
                            '& .MuiTableBody-root .MuiTableRow-root': {
                                height: 36,
                            },
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 500}}>Company Name</TableCell>
                                <TableCell sx={{fontWeight: 500}}>Company Chinese Name</TableCell>
                                <TableCell sx={{fontWeight: 500}}>Records</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && [...Array(rowsPerPage).keys()].map((i) => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                </TableRow>
                            ))}
                            {!loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        No data
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && (() => {
                                const grouped: Record<string, CompanyAnnualPage[]> = {};
                                rows.forEach(r => {
                                    const key = r.companyName || `unknown-${r.companyId}`;
                                    if (!grouped[key]) grouped[key] = [];
                                    grouped[key].push(r);
                                });
                                Object.values(grouped).forEach(list => list.sort((a, b) => {
                                    const ya = a.yearOfAssessment || '';
                                    const yb = b.yearOfAssessment || '';
                                    return yb.localeCompare(ya);
                                }));
                                return Object.entries(grouped).map(([companyName, annuals]) => {
                                    const isExpanded = expandedCompany === companyName;
                                    const first = annuals[0];
                                    return (
                                        <React.Fragment key={companyName}>
                                            <TableRow
                                                hover
                                                sx={{cursor: 'pointer'}}
                                                onClick={() => setExpandedCompany(isExpanded ? null : companyName)}
                                            >
                                                <TableCell>{companyName}</TableCell>
                                                <TableCell>{first?.companyChineseName || '-'}</TableCell>
                                                <TableCell sx={{color: 'text.secondary', fontSize: 12}}>
                                                    {annuals.length} record(s)
                                                </TableCell>
                                            </TableRow>
                                            {isExpanded && (
                                                <TableRow sx={{height: 'auto !important'}}>
                                                    <TableCell colSpan={3} sx={{bgcolor: 'background.default', p: 0, height: 'auto'}}>
                                                        <Box sx={{borderTop: `1px solid ${theme.palette.divider}`, pl: '80px'}}>
                                                            <Table
                                                                size="small"
                                                                sx={{
                                                                    '& .MuiTableCell-root': {
                                                                        paddingTop: '5px',
                                                                        paddingBottom: '5px',
                                                                    },
                                                                    '& .MuiTableBody-root .MuiTableRow-root': {
                                                                        height: 36,
                                                                    },
                                                                }}
                                                            >
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell sx={{fontWeight: 600}}>Assessment Year</TableCell>
                                                                        <TableCell sx={{fontWeight: 600}}>Principal Activities</TableCell>
                                                                        <TableCell sx={{fontWeight: 600}}>Presentation Currency</TableCell>
                                                                        <TableCell sx={{fontWeight: 600}}>Qualify Opinion</TableCell>
                                                                        <TableCell sx={{fontWeight: 600}}>Workflow</TableCell>
                                                                        <TableCell sx={{fontWeight: 600}}>Action</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {annuals.map((r, idx) => (
                                                                        <TableRow key={r.annualId ?? `row-${idx}`} hover>
                                                                            <TableCell>{r.yearOfAssessment || '-'}</TableCell>
                                                                            <TableCell>{
                                                                                r.principalActivities == null
                                                                                    ? '-'
                                                                                    : (principalActivitiesMap[String(r.principalActivities)] ?? String(r.principalActivities))
                                                                            }</TableCell>
                                                                            <TableCell>{r.presentationCurrency || '-'}</TableCell>
                                                                            <TableCell>{r.qualifyOpinion || '-'}</TableCell>
                                                                            <TableCell>
                                                                                <Typography variant="caption" sx={{
                                                                                    px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                                                                                    bgcolor: r.flowName === 'Audit' ? 'primary.light' : 'success.light',
                                                                                    color: r.flowName === 'Audit' ? 'primary.dark' : 'success.dark',
                                                                                }}>{r.flowName || '-'}</Typography>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="contained"
                                                                                        color="secondary"
                                                                                        sx={{
                                                                                            borderRadius: theme.shape.borderRadius,
                                                                                            textTransform: 'none',
                                                                                            fontWeight: 600,
                                                                                            boxShadow: theme.shadows[1],
                                                                                            minHeight: 28,
                                                                                            py: 0.25,
                                                                                            '&:hover': {boxShadow: theme.shadows[3]}
                                                                                        }}
                                                                                        onClick={() => {
                                                                                            if (r.annualId != null) {
                                                                                                setIsAdd(false);
                                                                                                setSelectedAnnualId(r.annualId);
                                                                                                setSelectedCompanyId(r.companyId);
                                                                                                setDetailOpen(true);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        Details
                                                                                    </Button>
                                                                                    {/*{canDelete && (*/}
                                                                                    <PopoverButton
                                                                                        nodeId={r.annualId as number}
                                                                                        nodeName={r.companyName || ''}
                                                                                        onRemove={async (id: number) => {
                                                                                            try {
                                                                                                await deleteAnnual(id);
                                                                                                setSnackbar({
                                                                                                    open: true,
                                                                                                    severity: 'success',
                                                                                                    message: 'Compan Annual Delete Successful！'
                                                                                                });
                                                                                                void fetchData({silent: true});
                                                                                            } catch (e: any) {
                                                                                                setSnackbar({
                                                                                                    open: true,
                                                                                                    severity: 'error',
                                                                                                    message: e?.message || 'Delete failed!'
                                                                                                });
                                                                                            }
                                                                                        }}
                                                                                        theme={theme}
                                                                                    />
                                                                                    {/*)}*/}
                                                                                </Stack>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                });
                            })()}
                        </TableBody>
                    </Table>
                </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        sx={{
                            flexShrink: 0,
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                m: 0,
                                fontSize: 12
                            },
                            '& .MuiTablePagination-actions .MuiIconButton-root': {p: 0.25},
                            minHeight: 32,
                            px: 1,
                        }}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                <CompanyAnnualDetail
                    open={detailOpen}
                    annualId={selectedAnnualId ?? undefined}
                    companyId={selectedCompanyId ?? undefined}
                    isAdd={isAdd}
                    onClose={(payload?: { message?: string; severity?: 'success' | 'error' }) => {
                        setDetailOpen(false);
                        setSelectedAnnualId(null);
                        setIsAdd(false);
                        if (payload?.message) {
                            setSnackbar({
                                open: true,
                                message: payload.message,
                                severity: payload.severity || 'success'
                            });
                        }
                        void fetchData({silent: true});
                    }}
                />
                <SnackbarField
                    open={!!snackbar.open}
                    message={snackbar.message || ''}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(s => ({...s, open: false}))}
                />
            </Box>
        </Slide>
    );
}
